/**
 * Normalization functions for input field values.
 */
interface Normalizers {
  [key: string]: (value: string | number) => string
}

/**
 * Strip non-digits from phone numbers.
 *
 * @param value a string or number
 * @returns a string of digits
 */
export const phone = (value: string | number): string => {
  if (typeof value === `number`) return `${value}`
  return value.replace(/[^0-9]/g, ``)
}

/**
 * Trim leading and trailing whitespace from the given string.
 *
 * @param value the input field's value, as a string
 * @returns a string
 */
export const text = (value: string): string => {
  return value.trim()
}

/**
 * Trim leading and trailing whitespace from the given string.
 *
 * @param value the textarea's value, as a string
 * @returns a string
 */
export const textarea = (value: string): string => {
  return text(value)
}

/**
 * Convert a time from 12hr format to 24hr format.
 *
 * @param value a time, formatted as HH:MM AM/PM
 * @returns string
 */
export const time = (value: string): string => {
  const [time, amPm] = value.split(` `)
  const [hr, minute] = time.split(`:`)
  const hour = amPm.toLowerCase() === `am` ? hr : String(parseInt(hr) + 12)
  return `${hour}:${minute}`
}

/**
 * Normalize a form's data.
 * Normalization is performed on fields that either a) have a name
 * that matches a key in the normalization dictionary, or b) have a
 * tagName that matches a key in the normalization dictionary.
 * Only 1 normalization function is called on a field.
 *
 * @param form an HTML form element
 * @param normalizers a dictionary of normalization functions. default
 * normalizers can be overridden here.
 * @returns a normalized FormData object
 */
export const getNormalizedFormData = (
  form: HTMLFormElement,
  normalizers: Normalizers = {}
): FormData => {
  const _normalizers = { phone, text, time, ...normalizers }

  const doNormalization = (field, value, fn, formData) => {
    const normalizedValue = _normalizers[fn](value)
    formData.set(field, normalizedValue)
  }

  const formData = new FormData(form)
  //@ts-ignore
  formData.entries().forEach(([field, value]) => {
    if (field in _normalizers) {
      doNormalization(field, value, field, formData)
      return
    }

    const input = form.querySelector<HTMLInputElement>(`input[name="${field}"]`)
    if (!input) return

    const inputType = input.type
    if (!(inputType in _normalizers)) return
    doNormalization(field, value, inputType, formData)
  })

  return formData
}

export default {
  getNormalizedFormData,
  phone,
  text,
  time,
}
